import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IHotelAutocompleteItem, IHotelAutocompleteResponse } from '@share/common-types';
import {
  SESSION_EXPIRED_MESSAGE,
  SESSION_EXPIRED_STATUS,
  SortTypes,
  Urls,
  SESSION_SEARCH_EXPIRED_MESSAGE,
  HomesTypes,
  ALL_INCLUSIVE_VALUE
} from '@share/constants';
import { getHeaders, axiosInstance, AppThunk, getFilters } from '@share/utils';

export enum HotelsFilterType {
  Normal,
  Sundance,
  B2C,
  B2cHotelOnly,
  NormalVacations,
  B2CVacations,
  NormalVacationsFiltered,
  B2CVacationsFiltered,
  Recommended,
  RecommendedCustomerAgent ,
  AllInclusive
}

export interface IFilterType {
  type: HotelsFilterType;
  list?: string[];
}

export interface IFiltersState {
  propertyName: string;
  distance: number;
  starRatings: number[];
  amenities: string[];
  accommodationType: string[];
  accessibility: string[];
  popularLocations: string[];
  refundable: string[];
  budget: string[];
  neighbourhoods: string[];
  places: string[];
  flexibleDates: string[];
  sortBy: SortTypes;
  autocompleteItems: IHotelAutocompleteItem[];
  autocompleteLoading: boolean;
  selectedAutocompleteItem: IHotelAutocompleteItem | null;
  selectedAutocompleteLabel: string;
  boardNames: string[];
  searchHomes: HomesTypes;
}

export const filtersInitialStateBase: IFiltersState = {
  sortBy: SortTypes.BiggestSavingsPercentage,
  propertyName: '',
  distance: 10,
  budget: [],
  neighbourhoods: [],
  places: [],
  starRatings: [],
  amenities: [],
  popularLocations: [],
  accommodationType: [],
  accessibility: [],
  refundable: [],
  autocompleteItems: [],
  flexibleDates: [],
  autocompleteLoading: false,
  selectedAutocompleteItem: null,
  selectedAutocompleteLabel: '',
  boardNames: [],
  searchHomes: HomesTypes.All
};

export const filtersInitialState: IFiltersState = {
  ...filtersInitialStateBase,
  sortBy: SortTypes.BiggestSavingsPercentage,
  searchHomes: HomesTypes.All
};

export const filtersB2CInitialState: IFiltersState = {
  ...filtersInitialState,
  sortBy: SortTypes.GuestRating,
  searchHomes: HomesTypes.All
};

export const filtersB2CHotelsOnlyInitialState: IFiltersState = {
  ...filtersInitialState,
  sortBy: SortTypes.GuestRating,
  searchHomes: HomesTypes.HotelOnly
};

export const filtersInitialVacationsState: IFiltersState = {
  ...filtersInitialStateBase,
  sortBy: SortTypes.BiggestSavingsPercentage,
  searchHomes: HomesTypes.VacationRentalOnly
};

export const filtersB2CVacationsInitialState: IFiltersState = {
  ...filtersInitialState,
  sortBy: SortTypes.GuestRating,
  searchHomes: HomesTypes.VacationRentalOnly
};

export const filtersSundanceInitialState: IFiltersState = {
  ...filtersInitialState,
  searchHomes: HomesTypes.HotelOnly
};

export const filtersRecommnededInitialState: IFiltersState = {
  ...filtersInitialState,
  sortBy: SortTypes.Recommended,
};

export const filtersRecommnededCustomerAgentInitialState: IFiltersState = {
  ...filtersInitialState,
  sortBy: SortTypes.RecommendedCustomerAgent,
};

export const VACATIONS_FILTERED = ['Apartment', 'Cottage', 'Villa', 'Private vacation home' ];

export const filtersInitialVacationsFilteredState: IFiltersState = {
  ...filtersInitialVacationsState,
  accommodationType: [...VACATIONS_FILTERED]
};

export const filtersB2CVacationsInitialFilteredState: IFiltersState = {
  ...filtersB2CVacationsInitialState,
  accommodationType: [...VACATIONS_FILTERED]
};

export const filtersAllInclusiveInitialState: IFiltersState = {
  ...filtersInitialState,
  sortBy: SortTypes.PriceAsc,
  boardNames: [ALL_INCLUSIVE_VALUE]
};

const AUTO_COMPLETE_MAX_ITEMS = 5;
const zeroItem = 0;

const filtersSlice = createSlice({
  name: 'filters',
  initialState: filtersInitialState,
  reducers: {
    setSortBy: (state: IFiltersState, { payload }: PayloadAction<SortTypes>) => {
      state.sortBy = payload;
    },
    setPropertyName: (state: IFiltersState, { payload }: PayloadAction<string>) => {
      state.propertyName = payload;
    },
    setBudget: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.budget = payload;
    },
    setNeighbourhoods: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.neighbourhoods = payload;
    },
    setPopularLocations: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.popularLocations = payload;
    },
    setPlaces: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.places = payload;
    },
    setStarRating: (state: IFiltersState, { payload }: PayloadAction<number[]>) => {
      state.starRatings = payload;
    },
    setAmenities: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.amenities = payload;
    },
    setAccommodationType: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.accommodationType = payload;
    },
    setAccessibility: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.accessibility = payload;
    },
    setBoardName: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.boardNames = payload;
    },
    setRefundable: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.refundable = payload;
    },
    setFlexibleDate: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.flexibleDates = payload;
    },
    setDistance: (state: IFiltersState, { payload }: PayloadAction<number>) => {
      state.distance = payload;
    },
    resetFilters: () => {
      return filtersInitialState;
    },
    resetFiltersByType: (_state: IFiltersState, { payload }: PayloadAction<{filterType: IFilterType, isQuickEnabled: boolean}>) => {    
      let response = filtersInitialState;
      if (payload.filterType.type === HotelsFilterType.B2C) {
        response = filtersB2CInitialState;
      } else if (payload.filterType.type === HotelsFilterType.Recommended) {
        response = filtersRecommnededInitialState;
      } else if (payload.filterType.type === HotelsFilterType.RecommendedCustomerAgent) {
        response = filtersRecommnededCustomerAgentInitialState;
      } else if (payload.filterType.type === HotelsFilterType.Sundance) {
        response = filtersSundanceInitialState; 
      } else if (payload.filterType.type === HotelsFilterType.B2cHotelOnly) {
        response = filtersB2CHotelsOnlyInitialState;
      } else if (payload.filterType.type === HotelsFilterType.B2CVacations) {
        response = filtersB2CVacationsInitialState;
      } else if (payload.filterType.type === HotelsFilterType.NormalVacations) {
        response = filtersInitialVacationsState;
      } else if (payload.filterType.type === HotelsFilterType.NormalVacationsFiltered) {
        response = payload.filterType.list?.length ? { ...filtersInitialVacationsFilteredState, accommodationType: [...payload.filterType.list] } : filtersInitialVacationsFilteredState;
      } else if (payload.filterType.type === HotelsFilterType.B2CVacationsFiltered) {
        response = payload.filterType.list?.length ? { ...filtersB2CVacationsInitialFilteredState, accommodationType: [...payload.filterType.list] } : filtersB2CVacationsInitialFilteredState;
      } else if (payload.filterType.type === HotelsFilterType.AllInclusive) {
        response = filtersAllInclusiveInitialState;
      } else {
        response = filtersInitialState;
      }

      if (payload.isQuickEnabled) {
        response = { ...response, sortBy: SortTypes.MostBooked };
      }
      
      return response;
    },
    setFilters: (_state: IFiltersState, { payload }: PayloadAction<IFiltersState>) => {
      return payload;
    },
    setAutocompleteItems: (
      state: IFiltersState,
      { payload }: PayloadAction<IHotelAutocompleteItem[]>,
    ) => {
      state.autocompleteItems = payload;
    },
    setAutocompleteLoading: (state: IFiltersState, { payload }: PayloadAction<boolean>) => {
      state.autocompleteLoading = payload;
    },
    selectAutocompleteItem: (
      state: IFiltersState,
      { payload }: PayloadAction<IHotelAutocompleteItem | null>,
    ) => {
      state.selectedAutocompleteItem = payload;
    },
    selectAutocompleteLabel: (state: IFiltersState, { payload }: PayloadAction<string>) => {
      state.selectedAutocompleteLabel = payload;
    },
    setSearchHomes: (state: IFiltersState, { payload }: PayloadAction<HomesTypes>) => {
      state.searchHomes = payload;
    }
  },
});

export const filtersActions = filtersSlice.actions;

export const filtersReducer = filtersSlice.reducer;

export const getHotelsAutocomplete = (search: string): AppThunk => {
  return async (dispatch, getState) => {
    try {
      const { filtersStore, hotelsStore, locationsStore, loginStore } = getState();
      const { searchHomes } = filtersStore;
      const {quickSessionKey} = hotelsStore;

      let loadQuick = loginStore.account.hasQuick;
      const isQuickEnabled=loadQuick? true:false;
  
      dispatch(filtersActions.setAutocompleteLoading(true));

      const res: { data: IHotelAutocompleteResponse, status: number } = await axiosInstance.post(
        Urls.HotelAutocomplete,
        {
          count: AUTO_COMPLETE_MAX_ITEMS,
          sessionKey: hotelsStore.sessionKey,
          isQuickEnabled,
          quickSessionKey,
          location: hotelsStore.lastSearchedLocation
            ? { ...hotelsStore.lastSearchedLocation.geoLocation, type: 'City' }
            : { ...locationsStore.selectedLocation.geoLocation, type: 'City' },
          filter: {
            ...getFilters(filtersStore),
            propertyName: search,
          },
          searchHomes,
        },
        {
          ...getHeaders(),
        },
      );

      dispatch(filtersActions.setAutocompleteItems(res?.status === 200 ? res.data.hotelDetails : []));
      dispatch(filtersActions.setAutocompleteLoading(false));
    } catch (error: any) {
      dispatch(filtersActions.setAutocompleteLoading(false));
      console.error(error);

      // error when user session ended
      if (
        error?.response?.status === SESSION_EXPIRED_STATUS &&
        error?.response?.data &&
        (error?.response?.data[zeroItem] === SESSION_EXPIRED_MESSAGE ||
          error?.response?.data[zeroItem]?.errorMessage === SESSION_SEARCH_EXPIRED_MESSAGE)
      ) {
        window.location.reload();
      }
    }
  };
};
